import * as React from 'react';
// mui
import {
  Button,
  Divider,
  DialogContent,
  TextField,
  DialogActions,
  Grid,
  Dialog,
  DialogTitle,
  useTheme,
  useMediaQuery,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  DatePicker,
  DateTimePicker,
  LocalizationProvider
} from '@mui/x-date-pickers';
// icons
import FlagIcon from '@mui/icons-material/Flag';
// redux
import { useSelector } from 'react-redux';
import { selectTaskById } from '../../../store/selectors/taskSelectors';
import { selectEventById } from '../../../store/selectors/eventSelectors';
import { selectAllProjects, selectSelectedProjectId } from '../../../store/selectors/projectSelectors';
// dayjs
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);
dayjs.extend(isSameOrAfter);
// types
import Event from '../../../interfaces/Event.interface';
import Task from '../../../interfaces/Task.interface';
import Project from '../../../interfaces/Project.interface';
// axios
import api from '../../../axios';
// components
import { desktopSliderMarks, mobileSliderMarks } from '../../common/sliderMarks';
import ItemBreadcrumbs from '../../common/ItemBreadcrumbs';

// Empty Event Data
const emptyEventData: Event = {
  id: 0,
  title: '',
  description: '',
  priority: 3,
  startTime: '',
  endTime: '',
  parentTaskId: null,
  createdDate: null,
  updatedDate: null,
  completedDate: null,
  projectId: null,
  completed: false,
  excused: false,
  excuseExpiresAt: null,
};

interface EventDialogProps {
  onSave: () => void;
  onCancel: () => void;
  addMode: boolean;
  parentTaskId: number | null;
  eventId: number | null; // can be null if adding
}

const isFullDaySpan = (start: dayjs.Dayjs, end: dayjs.Dayjs): boolean => {
  return start.isSame(start.startOf('day')) &&
          end.isSame(start.endOf('day'));
};

export default function EventDialog(props: EventDialogProps) {
  // props
  const { onSave, onCancel, addMode, parentTaskId, eventId } = props;

  // hooks
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // redux
  const selectEvent = eventId ? selectEventById(eventId) : () => null;
  const rawEventData: Event = useSelector(selectEvent) as Event;
  const originalEventData: Event = eventId !== null ? rawEventData : emptyEventData;

  const selectParentTask = parentTaskId
    ? selectTaskById(parentTaskId)
    : () => null;
  const parentTaskRaw = useSelector(selectParentTask);
  const parentTaskData: Task | null = parentTaskRaw ?? null;
  
  const localProjects: Project[] = useSelector(selectAllProjects());
  const localSelectedProjectId: number | null = useSelector(selectSelectedProjectId());

  // local state
  const determineEventProjectId = () => {
    if (parentTaskData) { // use parent project
      return parentTaskData.projectId;
    } else if (addMode) { // if new task, use currently selected project
      return localSelectedProjectId;
    } else { // if task has no parent and already exists
      return originalEventData.projectId;
    }
  }

  const [ eventData, setEventData ] = React.useState<Event>({
    ...originalEventData,
    parentTaskId: parentTaskId ?? null,
    projectId: determineEventProjectId(),
  });
  const [ open, setOpen ] = React.useState(true);
  const [ loading, setLoading ] = React.useState(false);
  const [ isAllDay, setIsAllDay ] = React.useState(isFullDaySpan(dayjs(eventData.startTime), dayjs(eventData.endTime)));

  const handleDialogClose = () => {
    onCancel();
    setOpen(false);
    setEventData(originalEventData);
  }

  const handleProjectChange = (event: any) => {
    setEventData({
      ...eventData,
      projectId: event.target.value
    });
  }

  const handlePriorityChange = (event: any) => {
    setEventData({
      ...eventData,
      priority: event.target.value
    });
  }

  const handleDialogSave = () => {
    setLoading(true);

    let saveData: Event = { ...eventData };

    if (saveData.projectId === 0) {
      saveData.projectId = null;
    }

    if (addMode) {
      api.post(`/events/create`, saveData)
        .then((res) => {
          console.log(res);
          
          setOpen(false);
          setEventData(originalEventData);
          setLoading(false);
          onSave();
        })
        .catch((err) => {
          console.error(err);
          setLoading(false);
        });
    } else {
      api.put(`/events/update/${saveData.id}`, saveData)
        .then((res) => {
          console.log(res);

          setOpen(false);
          setEventData(originalEventData);
          setLoading(false);
          onSave();
        })
        .catch((err) => {
          console.error(err);
          setLoading(false);
        });
    }
  }

  const handleAllDayCheckboxClick = (event: any) => {
    const checked = event.target.checked;
    setIsAllDay(checked);

    if (checked) {
      const currentStart = eventData.startTime
        ? dayjs.utc(eventData.startTime).local()
        : dayjs(); // fallback to today

      const startOfDay = currentStart.startOf('day');
      const endOfDay = currentStart.endOf('day');

      setEventData({
        ...eventData,
        startTime: startOfDay.utc().toISOString(),
        endTime: endOfDay.utc().toISOString(),
      });
    }
  }

  const handleAllDayDateChange = (val: Dayjs | null) => {
    if (val && val.isValid()) {
      const startOfDay = val.startOf('day');
      const endOfDay = val.endOf('day');
      setEventData({
        ...eventData,
        startTime: startOfDay.utc().toISOString(),
        endTime: endOfDay.utc().toISOString(),
      });
    } else {
      setEventData({
        ...eventData,
        startTime: '',
        endTime: '',
      });
    }
  }

  const handleStartTimeChange = (val: Dayjs | null) => {
    if (val && val.isValid()) {
      setEventData({ ...eventData, startTime: val.utc().toISOString() });
    } else {
      setEventData({ ...eventData, startTime: '' });
    }
  }

  const handleEndTimeChange = (val: Dayjs | null) => {
    if (val && val.isValid()) {
      setEventData({ ...eventData, endTime: val.utc().toISOString() });
    } else {
      setEventData({ ...eventData, endTime: '' });
    }
  }

  return (
    <Dialog
      open={open}
      fullScreen={isMobile}
      onClose={handleDialogClose}
      fullWidth
      slotProps={{
        paper: {
          component: 'form',
          onSubmit: (e: React.FormEvent<HTMLFormElement>) => e.preventDefault(),
        },
      }}
    >
      <DialogTitle sx={{ pb: 1, pt: 1 }}>
        <ItemBreadcrumbs
          currentItem={eventData}
          itemType="event"
          includeCurrentItem={false}
        />
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ px: isMobile ? 2 : '' }}>
        <Grid container spacing={2} sx={{ marginTop: 0 }}>
          <Grid size={isMobile ? 12 : 8}>
            <TextField
              label="Title"
              value={eventData.title}
              onChange={(e) => setEventData({ ...eventData, title: e.target.value })}
              margin="dense"
              fullWidth
              required
              variant="standard"
              autoFocus
            />
          </Grid>
          <Grid size={isMobile ? 12 : 4}>
            <FormControl fullWidth>
              <InputLabel id="demo-simple-select-label">Project</InputLabel>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                value={eventData.projectId}
                label="Project"
                disabled={parentTaskId !== null}
                onChange={handleProjectChange}
              >
                { localProjects.map((project: Project) => (
                  <MenuItem key={project.id} value={project.id}>{project.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ marginTop: 2 }}>
          <Grid size={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isAllDay}
                  onChange={handleAllDayCheckboxClick}
                  color="primary"
                />
              }
              label="All Day Event"
            />
          </Grid>

          {isAllDay ? (
            <Grid size={12} sx={{ pb: 1 }}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Date"
                  sx={{ width: '100%' }}
                  format="YYYY/MM/DD"
                  value={eventData.startTime ? dayjs.utc(eventData.startTime).local() : null}
                  onChange={handleAllDayDateChange}
                />
              </LocalizationProvider>
            </Grid>
          ) : (
            <Grid container sx={{ pb: 1, width: '100%' }}>
              <Grid size={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DateTimePicker
                    label="Start Time"
                    sx={{ width: '100%' }}
                    format="YYYY/MM/DD HH:mm"
                    value={eventData.startTime ? dayjs.utc(eventData.startTime).local() : null}
                    onChange={handleStartTimeChange}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid size={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DateTimePicker
                    label="End Time"
                    sx={{ width: '100%' }}
                    format="YYYY/MM/DD HH:mm"
                    value={eventData.endTime ? dayjs.utc(eventData.endTime).local() : null}
                    onChange={handleEndTimeChange}
                  />
                </LocalizationProvider>
              </Grid>
            </Grid>
          )}
        </Grid>

        <TextField
          label="Description"
          value={eventData.description}
          onChange={(e) => setEventData({ ...eventData, description: e.target.value })}
          margin="dense"
          fullWidth
          multiline
          rows={4}
          variant="outlined"
        />

        <Grid container spacing={1} sx={{ paddingX: isMobile ? 2 : 3, pl: 0, paddingY: 1, width: '100%' }}>
          <Grid size={1.5}>
            <FlagIcon sx={{ mt: isMobile ? 1.5 : 0.5 }} />
          </Grid>
          <Grid size={10.5}>
            <Slider
              aria-label="Priority"
              value={eventData.priority}
              onChange={handlePriorityChange}
              defaultValue={1}
              min={1}
              max={5}
              step={1}
              valueLabelDisplay="auto"
              marks={isMobile ? mobileSliderMarks.priority : desktopSliderMarks.priority}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={handleDialogClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleDialogSave}
          disabled={
            !eventData.title ||
            !eventData.startTime ||
            !eventData.endTime ||
            dayjs(eventData.startTime).isAfter(dayjs(eventData.endTime))
          }
          loading={loading}
        >
          {addMode ? 'Create' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
